import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import typography from 'core/themes/base/typography'
import Theme from 'core/themes/model'

export type TextVariant = keyof typeof typography

interface TextProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  className?: string
  variant?: TextVariant
  component?: React.ElementType
  noWrap?: boolean
  lineClamp?: number
  maxWidth?: number
}

const variantMap = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  subtitle1: 'h5',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
}

// forwardRef required for mask from Input component to work
const Text = forwardRef<HTMLElement, TextProps>((props, ref) => {
  const {
    className,
    variant = 'body1',
    component = variantMap[variant] || 'span',
    noWrap = false,
    lineClamp = undefined,
    maxWidth,
    children,
    ...rest
  } = props
  const classes = useStyles()

  return React.createElement(
    component,
    {
      variant: variant,
      className: clsx(classes[variant], className, {
        [classes.noWrap]: noWrap,
        [classes.lineClamp]: lineClamp,
      }),
      ref: ref,
      style: maxWidth ? { maxWidth: maxWidth } : null,
      ...rest,
    },
    children,
  )
})

export default Text

const useStyles = makeStyles<Theme>((theme) => ({
  lineClamp: {
    // display: '-webkit-box',
    // '-webkit-line-clamp': ({ lineClamp }) => lineClamp,
    // '-webkit-box-orient': 'vertical',
    // overflow: 'hidden',
    wordBreak: 'break-word',
  },
  noWrap: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Text is used frequently within the page & usage of functions
  // within the CSS greatly slows down rendering, so manually
  // do the spread that we used the functions for in the CSS instead
  // These need to use optional chaining as the theme isn't loaded on first render.
  h1: {
    fontFamily: theme?.typography?.h1?.fontFamily,
    fontSize: theme?.typography?.h1?.fontSize,
    fontWeight: theme?.typography?.h1?.fontWeight,
    fontStretch: theme?.typography?.h1?.fontStretch,
    fontStyle: theme?.typography?.h1?.fontStyle,
    lineHeight: theme?.typography?.h1?.lineHeight,
    letterSpacing: theme?.typography?.h1?.letterSpacing,
    textAlign: theme?.typography?.h1?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  h2: {
    fontFamily: theme?.typography?.h2?.fontFamily,
    fontSize: theme?.typography?.h2?.fontSize,
    fontWeight: theme?.typography?.h2?.fontWeight,
    fontStretch: theme?.typography?.h2?.fontStretch,
    fontStyle: theme?.typography?.h2?.fontStyle,
    lineHeight: theme?.typography?.h2?.lineHeight,
    letterSpacing: theme?.typography?.h2?.letterSpacing,
    textAlign: theme?.typography?.h2?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  h3: {
    fontFamily: theme?.typography?.h3?.fontFamily,
    fontSize: theme?.typography?.h3?.fontSize,
    fontWeight: theme?.typography?.h3?.fontWeight,
    fontStretch: theme?.typography?.h3?.fontStretch,
    fontStyle: theme?.typography?.h3?.fontStyle,
    lineHeight: theme?.typography?.h3?.lineHeight,
    letterSpacing: theme?.typography?.h3?.letterSpacing,
    textAlign: theme?.typography?.h3?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  h4: {
    fontFamily: theme?.typography?.h4?.fontFamily,
    fontSize: theme?.typography?.h4?.fontSize,
    fontWeight: theme?.typography?.h4?.fontWeight,
    fontStretch: theme?.typography?.h4?.fontStretch,
    fontStyle: theme?.typography?.h4?.fontStyle,
    lineHeight: theme?.typography?.h4?.lineHeight,
    letterSpacing: theme?.typography?.h4?.letterSpacing,
    textAlign: theme?.typography?.h4?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  subtitle1: {
    fontFamily: theme?.typography?.subtitle1?.fontFamily,
    fontSize: theme?.typography?.subtitle1?.fontSize,
    fontWeight: theme?.typography?.subtitle1?.fontWeight,
    fontStretch: theme?.typography?.subtitle1?.fontStretch,
    fontStyle: theme?.typography?.subtitle1?.fontStyle,
    lineHeight: theme?.typography?.subtitle1?.lineHeight,
    letterSpacing: theme?.typography?.subtitle1?.letterSpacing,
    textAlign: theme?.typography?.subtitle1?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  inputPlaceholder: {
    fontFamily: theme?.typography?.inputPlaceholder?.fontFamily,
    fontSize: theme?.typography?.inputPlaceholder?.fontSize,
    fontWeight: theme?.typography?.inputPlaceholder?.fontWeight,
    fontStretch: theme?.typography?.inputPlaceholder?.fontStretch,
    fontStyle: theme?.typography?.inputPlaceholder?.fontStyle,
    lineHeight: theme?.typography?.inputPlaceholder?.lineHeight,
    letterSpacing: theme?.typography?.inputPlaceholder?.letterSpacing,
    textAlign: theme?.typography?.inputPlaceholder?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  subtitle2: {
    fontFamily: theme?.typography?.subtitle2?.fontFamily,
    fontSize: theme?.typography?.subtitle2?.fontSize,
    fontWeight: theme?.typography?.subtitle2?.fontWeight,
    fontStretch: theme?.typography?.subtitle2?.fontStretch,
    fontStyle: theme?.typography?.subtitle2?.fontStyle,
    lineHeight: theme?.typography?.subtitle2?.lineHeight,
    letterSpacing: theme?.typography?.subtitle2?.letterSpacing,
    textAlign: theme?.typography?.subtitle2?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  nav: {
    fontFamily: theme?.typography?.nav?.fontFamily,
    fontSize: theme?.typography?.nav?.fontSize,
    fontWeight: theme?.typography?.nav?.fontWeight,
    fontStretch: theme?.typography?.nav?.fontStretch,
    fontStyle: theme?.typography?.nav?.fontStyle,
    lineHeight: theme?.typography?.nav?.lineHeight,
    letterSpacing: theme?.typography?.nav?.letterSpacing,
    textAlign: theme?.typography?.nav?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  buttonPrimary: {
    fontFamily: theme?.typography?.buttonPrimary?.fontFamily,
    fontSize: theme?.typography?.buttonPrimary?.fontSize,
    fontWeight: theme?.typography?.buttonPrimary?.fontWeight,
    fontStretch: theme?.typography?.buttonPrimary?.fontStretch,
    fontStyle: theme?.typography?.buttonPrimary?.fontStyle,
    lineHeight: theme?.typography?.buttonPrimary?.lineHeight,
    letterSpacing: theme?.typography?.buttonPrimary?.letterSpacing,
    textAlign: theme?.typography?.buttonPrimary?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  body1: {
    fontFamily: theme?.typography?.body1?.fontFamily,
    fontSize: theme?.typography?.body1?.fontSize,
    fontWeight: theme?.typography?.body1?.fontWeight,
    fontStretch: theme?.typography?.body1?.fontStretch,
    fontStyle: theme?.typography?.body1?.fontStyle,
    lineHeight: theme?.typography?.body1?.lineHeight,
    letterSpacing: theme?.typography?.body1?.letterSpacing,
    textAlign: theme?.typography?.body1?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  caption1: {
    fontFamily: theme?.typography?.caption1?.fontFamily,
    fontSize: theme?.typography?.caption1?.fontSize,
    fontWeight: theme?.typography?.caption1?.fontWeight,
    fontStretch: theme?.typography?.caption1?.fontStretch,
    fontStyle: theme?.typography?.caption1?.fontStyle,
    lineHeight: theme?.typography?.caption1?.lineHeight,
    letterSpacing: theme?.typography?.caption1?.letterSpacing,
    textAlign: theme?.typography?.caption1?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  buttonSecondary: {
    fontFamily: theme?.typography?.buttonSecondary?.fontFamily,
    fontSize: theme?.typography?.buttonSecondary?.fontSize,
    fontWeight: theme?.typography?.buttonSecondary?.fontWeight,
    fontStretch: theme?.typography?.buttonSecondary?.fontStretch,
    fontStyle: theme?.typography?.buttonSecondary?.fontStyle,
    lineHeight: theme?.typography?.buttonSecondary?.lineHeight,
    letterSpacing: theme?.typography?.buttonSecondary?.letterSpacing,
    textAlign: theme?.typography?.buttonSecondary?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  inputTable: {
    fontFamily: theme?.typography?.inputTable?.fontFamily,
    fontSize: theme?.typography?.inputTable?.fontSize,
    fontWeight: theme?.typography?.inputTable?.fontWeight,
    fontStretch: theme?.typography?.inputTable?.fontStretch,
    fontStyle: theme?.typography?.inputTable?.fontStyle,
    lineHeight: theme?.typography?.inputTable?.lineHeight,
    letterSpacing: theme?.typography?.inputTable?.letterSpacing,
    textAlign: theme?.typography?.inputTable?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  body2: {
    fontFamily: theme?.typography?.body2?.fontFamily,
    fontSize: theme?.typography?.body2?.fontSize,
    fontWeight: theme?.typography?.body2?.fontWeight,
    fontStretch: theme?.typography?.body2?.fontStretch,
    fontStyle: theme?.typography?.body2?.fontStyle,
    lineHeight: theme?.typography?.body2?.lineHeight,
    letterSpacing: theme?.typography?.body2?.letterSpacing,
    textAlign: theme?.typography?.body2?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  sidenav1: {
    fontFamily: theme?.typography?.sidenav1?.fontFamily,
    fontSize: theme?.typography?.sidenav1?.fontSize,
    fontWeight: theme?.typography?.sidenav1?.fontWeight,
    fontStretch: theme?.typography?.sidenav1?.fontStretch,
    fontStyle: theme?.typography?.sidenav1?.fontStyle,
    lineHeight: theme?.typography?.sidenav1?.lineHeight,
    letterSpacing: theme?.typography?.sidenav1?.letterSpacing,
    textAlign: theme?.typography?.sidenav1?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  sidenav2: {
    fontFamily: theme?.typography?.sidenav2?.fontFamily,
    fontSize: theme?.typography?.sidenav2?.fontSize,
    fontWeight: theme?.typography?.sidenav2?.fontWeight,
    fontStretch: theme?.typography?.sidenav2?.fontStretch,
    fontStyle: theme?.typography?.sidenav2?.fontStyle,
    lineHeight: theme?.typography?.sidenav2?.lineHeight,
    letterSpacing: theme?.typography?.sidenav2?.letterSpacing,
    textAlign: theme?.typography?.sidenav2?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  inputLabel: {
    fontFamily: theme?.typography?.inputLabel?.fontFamily,
    fontSize: theme?.typography?.inputLabel?.fontSize,
    fontWeight: theme?.typography?.inputLabel?.fontWeight,
    fontStretch: theme?.typography?.inputLabel?.fontStretch,
    fontStyle: theme?.typography?.inputLabel?.fontStyle,
    lineHeight: theme?.typography?.inputLabel?.lineHeight,
    letterSpacing: theme?.typography?.inputLabel?.letterSpacing,
    textAlign: theme?.typography?.inputLabel?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  caption2: {
    fontFamily: theme?.typography?.caption2?.fontFamily,
    fontSize: theme?.typography?.caption2?.fontSize,
    fontWeight: theme?.typography?.caption2?.fontWeight,
    fontStretch: theme?.typography?.caption2?.fontStretch,
    fontStyle: theme?.typography?.caption2?.fontStyle,
    lineHeight: theme?.typography?.caption2?.lineHeight,
    letterSpacing: theme?.typography?.caption2?.letterSpacing,
    textAlign: theme?.typography?.caption2?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  caption3: {
    fontFamily: theme?.typography?.caption3?.fontFamily,
    fontSize: theme?.typography?.caption3?.fontSize,
    fontWeight: theme?.typography?.caption3?.fontWeight,
    fontStretch: theme?.typography?.caption3?.fontStretch,
    fontStyle: theme?.typography?.caption3?.fontStyle,
    lineHeight: theme?.typography?.caption3?.lineHeight,
    letterSpacing: theme?.typography?.caption3?.letterSpacing,
    textAlign: theme?.typography?.caption3?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
  caption4: {
    fontFamily: theme?.typography?.caption4?.fontFamily,
    fontSize: theme?.typography?.caption4?.fontSize,
    fontWeight: theme?.typography?.caption4?.fontWeight,
    fontStretch: theme?.typography?.caption4?.fontStretch,
    fontStyle: theme?.typography?.caption4?.fontStyle,
    lineHeight: theme?.typography?.caption4?.lineHeight,
    letterSpacing: theme?.typography?.caption4?.letterSpacing,
    textAlign: theme?.typography?.caption4?.textAlign,
    color: theme?.components?.typography?.default,
    margin: 0,
  },
}))
